import { useMemo, useState } from "react";
import {
  canNativeShare,
  copyTextToClipboard,
  emailShareUrl,
  facebookShareUrl,
  formatEventShareMessage,
  formatEventShareText,
  getDefaultEventShareUrl,
  linkedInShareUrl,
  nativeShareEvent,
  whatsAppShareUrl,
  xShareUrl,
  type EventShareOptions,
} from "../lib/eventShare";

type Props = {
  dateIso?: string;
  eventTitle?: string | null;
  url?: string;
  className?: string;
};

function openShareWindow(href: string) {
  window.open(href, "_blank", "noopener,noreferrer,width=600,height=520");
}

export function EventShareButtons({
  dateIso,
  eventTitle,
  url: urlProp,
  className,
}: Props) {
  const [copied, setCopied] = useState(false);
  const url = urlProp || getDefaultEventShareUrl();
  const shareOpts: EventShareOptions = useMemo(
    () => ({ dateIso, eventTitle, url }),
    [dateIso, eventTitle, url],
  );
  const message = useMemo(() => formatEventShareMessage(shareOpts), [shareOpts]);
  const tweetText = useMemo(
    () => formatEventShareText({ dateIso, eventTitle }),
    [dateIso, eventTitle],
  );
  const showNative = canNativeShare();

  async function onCopyLink() {
    const ok = await copyTextToClipboard(url);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  async function onNativeShare() {
    await nativeShareEvent(shareOpts);
  }

  const rootClass = ["rsvp-modal-share", className].filter(Boolean).join(" ");

  return (
    <div className={rootClass}>
      <div className="rsvp-modal-share-k">Share this event</div>
      <div className="rsvp-modal-share-row" role="group" aria-label="Share event">
        {showNative && (
          <button
            type="button"
            className="rsvp-modal-share-btn"
            onClick={() => void onNativeShare()}
            aria-label="Share via device"
          >
            <ShareIcon />
            <span>Share</span>
          </button>
        )}
        <button
          type="button"
          className="rsvp-modal-share-btn"
          onClick={() => openShareWindow(whatsAppShareUrl(message))}
          aria-label="Share on WhatsApp"
        >
          <WhatsAppIcon />
          <span>WhatsApp</span>
        </button>
        <button
          type="button"
          className="rsvp-modal-share-btn"
          onClick={() => openShareWindow(facebookShareUrl(url))}
          aria-label="Share on Facebook"
        >
          <FacebookIcon />
          <span>Facebook</span>
        </button>
        <button
          type="button"
          className="rsvp-modal-share-btn"
          onClick={() => openShareWindow(xShareUrl(tweetText, url))}
          aria-label="Share on X"
        >
          <XIcon />
          <span>X</span>
        </button>
        <button
          type="button"
          className="rsvp-modal-share-btn"
          onClick={() => openShareWindow(linkedInShareUrl(url))}
          aria-label="Share on LinkedIn"
        >
          <LinkedInIcon />
          <span>LinkedIn</span>
        </button>
        <button
          type="button"
          className="rsvp-modal-share-btn"
          onClick={() => openShareWindow(emailShareUrl("Together In Heaven", message))}
          aria-label="Share by email"
        >
          <EmailIcon />
          <span>Email</span>
        </button>
        <button
          type="button"
          className={`rsvp-modal-share-btn${copied ? " is-copied" : ""}`}
          onClick={() => void onCopyLink()}
          aria-label={copied ? "Link copied" : "Copy event link"}
        >
          <LinkIcon />
          <span>{copied ? "Copied" : "Copy link"}</span>
        </button>
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
      <path d="M16 3C8.8 3 3 8.8 3 16c0 2.3.6 4.4 1.7 6.3L3 29l6.9-1.7C11.7 28.4 13.8 29 16 29c7.2 0 13-5.8 13-13S23.2 3 16 3zm7.4 18.3c-.3.9-1.6 1.7-2.3 1.8-.6.1-1.4.1-2.2-.1-.5-.2-1.2-.4-2-.8-3.6-1.5-5.9-5.2-6.1-5.4-.2-.2-1.5-2-1.5-3.8s.9-2.7 1.3-3.1c.3-.3.7-.4 1-.4h.7c.2 0 .5-.1.8.6.3.7 1 2.4 1.1 2.6.1.2.1.4 0 .6-.1.2-.2.4-.4.6-.2.2-.4.5-.5.6-.2.2-.4.4-.2.7.2.4.9 1.5 2 2.4 1.4 1.2 2.5 1.6 2.9 1.8.4.2.6.2.8-.1.2-.3.9-1 1.1-1.4.2-.4.4-.3.7-.2.3.1 1.9.9 2.3 1.1.3.2.6.2.6.4.1.1.1.9-.2 1.8z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95 0-5.52-4.48-10-10-10z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 1 1 6.5 4.75a1.75 1.75 0 0 1 0 3.5zM19 19h-3v-4.64c0-1.1-.02-2.53-1.54-2.53-1.54 0-1.77 1.2-1.77 2.45V19h-3v-9h2.89v1.23h.04c.4-.76 1.38-1.56 2.84-1.56 3.04 0 3.6 2 3.6 4.59V19z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 6h16v12H4V6zm0 0 8 7 8-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10 14a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1M14 10a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
